
import { getMessage } from '@tcc/i18n';
import Card from '../../../components/cards/cardAndPreview/Card';
import { RegionReportCard } from '../RegionReportCard/RegionReportCard';
import { EnhancedScoutingPin } from '../EnhancedScoutingPin/EnhancedScoutingPin';
import { NoActivities } from '../../../components/cards/Scouting/NoActivities';
import { openModal } from '../../../actions/modalActions';
import { setSelectedActivity } from '../../../actions/scoutingActions';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useAppDispatch, useAppSelector } from '../../../utils/useAppSelector';

import { getInversedFieldBoundary } from '../../../selectors/fieldSelectors';
import { getGeoJson } from '../../../utils/geo';
import { getActivitiesWithoutFieldNotes } from '../../../selectors';
import { getPinsActivities } from '../../../selectors/scoutingSelectors';
import { trackHeapEvent } from '../../../actions/analyticsActions';

import { Activity, ShapeType, FeatureFlagKey, ModalType, ModalMode, ModalSize, EnhancedActivity } from '../../../types';

const isScoutingPin = (activity: Activity) => activity.location?.type === ShapeType.POINT;

type EnhancedScoutingCardProps = {
  index: number;
}

export const EnhancedScoutingCard = ({
  index,
}: EnhancedScoutingCardProps) => {
  const dispatch = useAppDispatch();
  const scoutingRegionsFeatureFlag = useFeatureFlag(FeatureFlagKey.SCOUTING_REGIONS)
  const activities: (Activity | EnhancedActivity)[] = useAppSelector(
    state => scoutingRegionsFeatureFlag ? getActivitiesWithoutFieldNotes(state, { index }) : getPinsActivities(state, { index })
  )
  const fieldBoundary = useAppSelector(getInversedFieldBoundary);
  const locale = useAppSelector(state => state.app.locale ?? 'en-us');

  if (!activities.length) {
    return <NoActivities />
  }

  const showModal = (activity: Activity) => () => {
    const isPin = isScoutingPin(activity);
    const type = isPin ? ModalType.ENHANCED_SCOUTING_FORM : ModalType.SCOUTING_FORM;
    const mode = isPin ? ModalMode.SHOW_ENHANCED_PIN : ModalMode.SHOW_NOTE;


    dispatch(setSelectedActivity(index, activity.id))
    dispatch(
      openModal(index, null as never, {
        type,
        edit: true, // TODO: SENV-468 -> Pins will be editable
        size: ModalSize.M,
        mode,
        activity,
      })
    );
    dispatch(trackHeapEvent(isPin ? 'View Scouting PIN' : 'View Field Region Note'));
  };

  return (
    <Card cardType="enhanced-scouting" testId="activities" title={getMessage('card.enhancedScouting')}>
      {activities.map((activity) => isScoutingPin(activity) ? (
        <EnhancedScoutingPin
          key={activity.id}
          activity={activity}
          locale={locale}
          geoJson={getGeoJson([fieldBoundary, activity.location])}
          onPinClick={showModal(activity)}
        />
      ) : (
        <RegionReportCard
          key={activity.id}
          activity={activity}
          locale={locale}
          geoJson={getGeoJson([fieldBoundary, activity.location])}
          onRegionClick={showModal(activity)}
        />
      ))}
    </Card>
  )
}
